import React, { useState, useRef } from "react";
import { motion, useInView } from "framer-motion";
import "./Experience.css";
import logo from '../../images/logo.png'
// Simple SVG icons
const BriefcaseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M10 2L14 6V8H20C21.11 8 22 8.89 22 10V19C22 20.11 21.11 21 20 21H4C2.89 21 2 20.11 2 19V10C2 8.89 2.89 8 4 8H10V2M12 4V6H10V8H14V6H12V4Z"/>
  </svg>
);

const CalendarIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 3H18V1H16V3H8V1H6V3H5C3.89 3 3 3.9 3 5V19C3 20.1 3.89 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3M19 19H5V8H19V19Z"/>
  </svg>
);

const LocationIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22S19 14.25 19 9C19 5.13 15.87 2 12 2M12 11.5C10.62 11.5 9.5 10.38 9.5 9S10.62 6.5 12 6.5 14.5 7.62 14.5 9 13.38 11.5 12 11.5Z"/>
  </svg>
);

const ChevronDownIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M7.41 8.58L12 13.17L16.59 8.58L18 10L12 16L6 10L7.41 8.58Z"/>
  </svg>
);

// Map for tech stack icons - using proper names
const techIcons = {
  "React.js": "React",
  "React": "React",
  "TypeScript": "TS",
  "Next.js": "Next",
  "JavaScript": "JS",
  "HTML5": "HTML",
  "CSS3": "CSS",
  "Tailwind CSS": "Tailwind",
  "Material-UI": "MUI",
  "Redux": "Redux",
  "Context API": "Context",
  "REST APIs": "REST",
  "MySQL": "MySQL",
  "Git": "Git",
  "Webpack": "Webpack",
  "Framer Motion": "Framer",
  "GSAP": "GSAP",
  "SEO": "SEO",
  "Responsive Design": "RWD",
  "Performance Optimization": "Perf"
};

const ExperienceCard = ({ job, index }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const cardRef = useRef(null);
  const isInView = useInView(cardRef, { once: false, amount: 0.2 });
  
  // Determine if card should be on left or right side of timeline
  const isEven = index % 2 === 0;
  
  // Animation variants
  const cardVariants = {
    hidden: { 
      opacity: 0, 
      x: isEven ? -30 : 30,
      y: 20
    },
    visible: { 
      opacity: 1, 
      x: 0,
      y: 0,
      transition: { 
        duration: 0.5, 
        ease: [0.25, 0.1, 0.25, 1],
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const contentVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        staggerChildren: 0.1,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <motion.div 
      ref={cardRef}
      className={`experience-card ${isEven ? 'card-left' : 'card-right'} ${isExpanded ? 'expanded' : ''}`}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={cardVariants}
      whileHover={{ scale: 1.01 }}
      transition={{ scale: { duration: 0.3 } }}
    >
      {/* Timeline connector and dot */}
      <div className="timeline-marker">
        <div className="timeline-dot">
          <BriefcaseIcon />
        </div>
      </div>
      
      {/* Card content */}
      <motion.div 
        className="card-content"
        variants={contentVariants}
      >
        {/* Card header with company and position */}
        <motion.div className="card-header" variants={itemVariants}>
          <div className="position-title">{job.position}</div>
          <div className="role-summary">{job.summary}</div>
          <div className="company-info">
            {job.logo && (
              <img src={job.logo} alt={`${job.company} logo`} className="company-logo" />
            )}
            <div className="company-details">
              {job.website ? (
                <a href={job.website} target="_blank" rel="noopener noreferrer" className="company-name-link">
                  {job.company}
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                    <polyline points="15,3 21,3 21,9"/>
                    <line x1="10" y1="14" x2="21" y2="3"/>
                  </svg>
                </a>
              ) : (
                <div className="company-name">{job.company}</div>
              )}
              <div className="company-description">{job.companyDesc}</div>
            </div>
          </div>
        </motion.div>
        
        {/* Card metadata */}
        <motion.div className="card-meta" variants={itemVariants}>
          <div className="meta-item">
            <CalendarIcon />
            <span>{job.year}</span>
          </div>
          <div className="meta-item">
            <LocationIcon />
            <span>{job.location}</span>
          </div>
        </motion.div>
        
        {/* Tech stack preview (only show 5 when collapsed) */}
        <motion.div className="tech-stack-preview" variants={itemVariants}>
          <div className="tech-icons-preview">
            {job.skills.slice(0, 5).map((skill, i) => (
              <motion.div 
                key={i} 
                className="tech-icon"
                whileHover={{ 
                  scale: 1.2,
                  y: -5,
                  transition: { type: "spring", stiffness: 300 }
                }}
                title={skill}
              >
                {techIcons[skill] || skill}
              </motion.div>
            ))}
            {job.skills.length > 5 && !isExpanded && (
              <div className="more-tech">+{job.skills.length - 5}</div>
            )}
          </div>
        </motion.div>
        
        {/* Expandable content */}
        <motion.div 
          className="expandable-content"
          initial={{ height: 0, opacity: 0 }}
          animate={{ 
            height: isExpanded ? "auto" : 0,
            opacity: isExpanded ? 1 : 0,
            marginTop: isExpanded ? "1rem" : 0
          }}
          transition={{ duration: 0.3 }}
        >
          {/* Full tech stack */}
          {job.skills.length > 5 && (
            <motion.div className="tech-stack" variants={itemVariants}>
              <h4>Technologies</h4>
              <div className="tech-icons">
                {job.skills.map((skill, i) => (
                  <motion.div 
                    key={i} 
                    className="tech-icon"
                    whileHover={{ 
                      scale: 1.2,
                      y: -5,
                      transition: { type: "spring", stiffness: 300 }
                    }}
                    title={skill}
                  >
                    {techIcons[skill] || skill}
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
          
          {/* Key achievements */}
          <motion.div className="achievements" variants={itemVariants}>
            <h4>Key Achievements</h4>
            <ul>
              {job.accomplishments.map((item, i) => (
                <motion.li 
                  key={i}
                  variants={itemVariants}
                  whileHover={{ x: 5 }}
                >
                  {item}
                </motion.li>
              ))}
            </ul>
          </motion.div>
        </motion.div>
        
        {/* Toggle button */}
        <button className="toggle-button" onClick={toggleExpand}>
          <span>{isExpanded ? "Show Less" : "Show More"}</span>
          <motion.div
            animate={{ rotate: isExpanded ? 180 : 0 }}
            transition={{ duration: 0.3 }}
          >
            <ChevronDownIcon />
          </motion.div>
        </button>
      </motion.div>
      
      {/* Decorative blob */}
      <div className="blob-decoration"></div>
    </motion.div>
  );
};

const Experience = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: false, amount: 0.1 });

  const jobs = [
    {
      year: "Feb 2025 - Mar 2026",
      position: "Frontend Engineer",
      summary: "Building multi-role HealthTech dashboards, eCommerce admin panels, and refactoring legacy React codebases for measurable performance gains.",
      company: "Helda Technology · Farmkrate · Hasini Info Tech",
      website: "https://www.farmkrate.in/",
      logo: logo,
      companyDesc: "Patented agri-tech platform connecting farmers and buyers. HealthTech and eCommerce contract roles delivering RBAC dashboards and scalable UI systems.",
      location: "Remote - Contract",
      accomplishments: [
        "Built multi-role React.js + TypeScript HealthTech dashboard with RBAC, protected routes, and REST API integration for Admin, Doctor, and Patient — improved task completion by 20%",
        "Architected eCommerce admin panel (Redux Toolkit + React Router DOM) with CRUD, inventory analytics, product search, pagination, and role-based access — cut manual tracking by 40% and order processing by 25%",
        "Translated Figma wireframes into pixel-perfect, WCAG-accessible components; resolved cross-browser issues and optimized REST API payloads — reduced API latency by 15%",
        "Refactored 3 legacy React codebases to functional components with hooks; introduced Webpack code-splitting and memoization (React.memo, useMemo, useCallback) — improved render performance by 30%"
      ],
      skills: ["React", "TypeScript", "Redux", "Context API", "REST APIs", "Tailwind CSS", "Material-UI", "Framer Motion", "GSAP", "Webpack", "Performance Optimization"]
    },
    {
      year: "Jan 2023 - Jan 2025",
      position: "Junior Frontend Developer",
      summary: "Led frontend of enterprise HRMS and built reusable component libraries with Tailwind CSS and MUI across 5+ product modules.",
      company: "Yatayati Info Solutions Pvt Ltd",
      website: "https://yatayati.com/",
      logo: "https://yatayati.com/favicon.ico",
      companyDesc: "Visionary collective crafting specialized apps, web solutions, embedded systems, and AI/ML innovations. Four transformative years redefining possibilities.",
      location: "Hyderabad, India",
      accomplishments: [
        "Led frontend of enterprise HRMS (React.js + Redux + TypeScript) for 200+ employees with attendance, leave, and payroll modules; RBAC for Admin, HR, and Employee roles — reduced report generation time by 50%",
        "Built reusable component library (30+ components) with Tailwind CSS + MUI across 5+ product modules — cut new-feature development time by 30% and enforced design consistency platform-wide",
        "Configured Babel + Webpack build pipeline; integrated Node.js REST APIs via Axios; wrote Jest + RTL unit tests covering all access-control and protected route logic — reduced build time by 20%"
      ],
      skills: ["React.js", "JavaScript", "TypeScript", "Redux", "Material-UI", "Tailwind CSS", "HTML5", "CSS3", "REST APIs", "MySQL", "Git", "Jest"]
    }
  ];

  return (
    <section id="experience" className="experience-section" ref={sectionRef}>
      {/* Background elements */}
      <div className="bg-blob blob-1"></div>
      <div className="bg-blob blob-2"></div>
      <div className="bg-blob blob-3"></div>
      
      <div className="experience-container">
        {/* Section header */}
        <motion.div 
          className="section-header"
          initial={{ opacity: 0, y: -30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: -30 }}
          transition={{ duration: 0.7, ease: "easeOut", delay: 0.2 }}
        >
          <motion.h2 
            className="section-title"
            initial={{ opacity: 0, y: -20 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: -20 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            Work Experience
          </motion.h2>
          <motion.div 
            className="title-underline"
            initial={{ width: 0 }}
            animate={isInView ? { width: "80px" } : { width: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          />
        </motion.div>
        
        {/* Timeline container */}
        <div className="timeline-container">
          {/* Central timeline line */}
          <motion.div 
            className="timeline-line"
            initial={{ height: 0 }}
            animate={isInView ? { height: "100%" } : { height: 0 }}
            transition={{ duration: 1.5, ease: "easeOut" }}
          />
          
          {/* Experience cards */}
          <div className="timeline-cards">
            {jobs.map((job, index) => (
              <ExperienceCard 
                key={index}
                job={job} 
                index={index}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Experience;